import React, { useState, useEffect, useRef, useCallback } from 'react';

const TIP_MAX_WIDTH = 320;
const TIP_MARGIN = 12;   // keep this clear of both screen edges
const TIP_GAP = 8;       // between the icon and the bubble

// Info icon + tooltip, extracted from KPICard so charts and other surfaces can
// reuse the same behaviour. `text` splits on `|` into separate lines.
export default function InfoTip({ text, label }) {
  // Click keeps the tooltip pinned (touch, and reading a long breakdown);
  // hover shows it without a click on desktop.
  const [showInfo, setShowInfo] = useState(false);
  const [hoverInfo, setHoverInfo] = useState(false);
  const [tipPos, setTipPos] = useState(null);
  const infoRef = useRef(null);
  const iconRef = useRef(null);
  const open = showInfo || hoverInfo;

  // The bubble used to be absolutely positioned at the icon's left edge, so on
  // a card in the right-hand column it ran past the screen and got cut off by
  // `.content`'s overflow. It is now fixed and measured against the viewport:
  // centred on the icon, clamped to stay fully on screen, and flipped below
  // the icon when there is no room above.
  const place = useCallback(() => {
    const el = iconRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = Math.min(TIP_MAX_WIDTH, window.innerWidth - TIP_MARGIN * 2);
    const left = Math.max(
      TIP_MARGIN,
      Math.min(r.left + r.width / 2 - width / 2, window.innerWidth - width - TIP_MARGIN)
    );
    setTipPos(r.top > 160
      ? { left, width, bottom: window.innerHeight - r.top + TIP_GAP }
      : { left, width, top: r.bottom + TIP_GAP });
  }, []);

  const openTip = useCallback((next) => {
    if (next) place();
    return next;
  }, [place]);

  useEffect(() => {
    if (!open) { setTipPos(null); return; }
    place();
    // Fixed positioning means the bubble would otherwise drift away from its
    // icon as the page moves underneath it.
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  useEffect(() => {
    if (!showInfo) return;
    function handleClick(e) {
      if (infoRef.current && !infoRef.current.contains(e.target)) setShowInfo(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('touchstart', handleClick);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('touchstart', handleClick); };
  }, [showInfo]);

  if (!text) return null;

  return (
    <span ref={infoRef} className="kpi-info" style={{ display: 'inline-block', marginLeft: '4px', verticalAlign: 'middle' }}
      onMouseEnter={() => { setHoverInfo(true); place(); }} onMouseLeave={() => setHoverInfo(false)}>
      <svg ref={iconRef} className="kpi-info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-light)" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" style={{ cursor: 'pointer', verticalAlign: 'middle' }}
        role="button" tabIndex={0} aria-label={`More info about ${label}`}
        onClick={() => setShowInfo(v => openTip(!v))}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowInfo(v => openTip(!v)); } }}>
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      {open && tipPos && (
        <div className="kpi-tooltip" style={{
          position: 'fixed',
          left: tipPos.left,
          width: tipPos.width,
          ...(tipPos.top != null ? { top: tipPos.top } : { bottom: tipPos.bottom }),
          background: 'var(--sidebar-bg, #1a202c)', color: 'white', padding: '8px 12px', borderRadius: '6px',
          fontSize: '11px', whiteSpace: 'normal',
          textAlign: 'left', zIndex: 400, boxShadow: 'var(--shadow-lg)',
          fontWeight: 400, lineHeight: '1.6',
        }}>
          {text.includes('|') ? text.split('|').map((line, i) => <div key={i}>{line.trim()}</div>) : text}
        </div>
      )}
    </span>
  );
}
